import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Button,
  CircularProgress,
  Typography,
  Alert,
  Modal,
  InputLabel,
  Snackbar,
} from "@mui/material";

import { addWorkspace, putWorkspace } from "../../actions/workspaceAction";
import { Link, useNavigate } from "react-router-dom";
import ModalBox from "../../themes/overrides/ModalBox";
import OutlinedInput from "../../themes/overrides/OutlinedInput";
import AddButton from "../../themes/overrides/AddButton";
import OutlinedAddButton from "../../themes/overrides/OutlinedAddButton";
import ReplayIcon from "@mui/icons-material/Replay";
import { addAssetFile } from "../../actions/mediaAction";

// Mirrors UploadFileValidationPipe on the server, minus the PDF it also allows:
// a workspace logo is an image.
const LOGO_TYPES = ["image/png", "image/jpg", "image/jpeg", "image/webp", "image/svg+xml"];
const LOGO_MAX_MB = 2;

const monogramFor = (name = "") => {
  const words = String(name).trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  if (words.length === 1) return words[0].slice(0, 2).toUpperCase();
  return (words[0][0] + words[1][0]).toUpperCase();
};

const WorkspaceModal = ({ isOpen, onClose, currentWorkspace, refreshList, isEdit = false }) => {
  const [workspace, setWorkspace] = useState({ name: "" });
  const [logoUrl, setLogoUrl] = useState(null);
  const [logoUploading, setLogoUploading] = useState(false);
  const fileInputRef = useRef(null);
  const error = useSelector((state) => state.workspace.error);
  const createError = useSelector((state) => state.workspace.createError);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [severity, setSeverity] = useState("success");
  const modalTitle = isEdit ? "Edit Workspace" : "Create Workspace";
  const buttonText = isEdit ? "Save Changes" : "Create New Workspace";

  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
    if (currentWorkspace) {
      setWorkspace(currentWorkspace);
    }
  }, [currentWorkspace]);

  useEffect(() => {
    if (isOpen && currentWorkspace?.data) {
      setWorkspace({ name: currentWorkspace.data.name });
      setLogoUrl(currentWorkspace.data.logoUrl || null);
    } else if (!isOpen) {
      setWorkspace({ name: "" });
      setLogoUrl(null);
    }
  }, [currentWorkspace, isOpen]);

  useEffect(() => {
    if (!isOpen) {
      dispatch({ type: 'CLEAR_WORKSPACE_ERROR' });
      setWorkspace({ name: "" });
    }
  }, [isOpen, dispatch]);


  useEffect(() => {
    if (error) {
      setErrorMessage(error);
      setSeverity("error");
      setOpenSnackbar(true);
    }
  }, [error]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setWorkspace((prev) => ({ ...prev, [name]: value }));
  };

  const showLogoError = (message) => {
    setErrorMessage(message);
    setSeverity("error");
    setOpenSnackbar(true);
  };

  /**
   * Uploads through the shared asset pipeline, so the logo lands in the
   * workspace's own media library like any other image. That endpoint rejects a
   * filename it has already seen in the workspace, so the file is given a
   * unique name on the way out — which also keeps successive logos from
   * overwriting one another in S3.
   */
  const handleLogoSelect = async (event) => {
    const file = event.target.files?.[0];
    // Let the same file be picked again after a remove or a failed upload.
    event.target.value = "";
    if (!file) return;

    if (!LOGO_TYPES.includes(file.type)) {
      showLogoError("Choose a PNG, JPG, WEBP or SVG image.");
      return;
    }
    if (file.size > LOGO_MAX_MB * 1024 * 1024) {
      showLogoError(`That image is over ${LOGO_MAX_MB}MB. Choose a smaller one.`);
      return;
    }

    const workspaceId = currentWorkspace?.data?._id;
    if (!workspaceId) {
      showLogoError("Save the workspace before adding a logo.");
      return;
    }

    setLogoUploading(true);
    try {
      const extension = file.name.includes(".") ? file.name.split(".").pop() : "png";
      const uniqueName = `workspace-logo-${Date.now()}.${extension}`;

      const formData = new FormData();
      formData.append("file", new File([file], uniqueName, { type: file.type }));
      formData.append("title", `${workspace.name || "Workspace"} logo`);
      formData.append("description", "Workspace logo");
      formData.append("workspaceId", workspaceId);

      const result = await dispatch(addAssetFile(formData));
      const uploadedUrl = result?.asset?.url;

      if (!uploadedUrl) {
        showLogoError(result?.message || "Could not upload that image.");
        return;
      }

      await dispatch(putWorkspace(workspaceId, uploadedUrl, "logoUrl"));
      setLogoUrl(uploadedUrl);
      setSuccessMessage("Logo updated");
      setSeverity("success");
      setOpenSnackbar(true);
      refreshList();
    } catch (uploadError) {
      showLogoError("Could not upload that image.");
    } finally {
      setLogoUploading(false);
    }
  };

  const handleLogoRemove = async () => {
    const workspaceId = currentWorkspace?.data?._id;
    if (!workspaceId) return;

    setLogoUploading(true);
    try {
      // The image itself stays in the media library; only the workspace stops
      // pointing at it, so the card falls back to the monogram.
      await dispatch(putWorkspace(workspaceId, null, "logoUrl"));
      setLogoUrl(null);
      setSuccessMessage("Logo removed");
      setSeverity("success");
      setOpenSnackbar(true);
      refreshList();
    } finally {
      setLogoUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (workspace.name.trim() == "") {
      dispatch({
        type: 'CREATE_WORKSPACE_FAIL',
        payload: "Workspace name cannot be empty or just spaces"
      });
      return; // Return to prevent submission
    }

    if (isEdit && currentWorkspace) {
      dispatch(putWorkspace(currentWorkspace.data._id, workspace.name, 'updateName'))
        .then(() => {
          setSuccessMessage("Workspace Updated");
          setOpenSnackbar(true);
          setSeverity("success");
          refreshList();
          onClose();
        })
    } else {
      dispatch(
        addWorkspace(workspace, navigate, (success) => {
          if (!success) return;

          setSuccessMessage("Workspace Created");
          setOpenSnackbar(true);
          setSeverity("success");
          setWorkspace({ name: "" });

          setTimeout(() => {
            refreshList();
            onClose();
            navigate("/space");
          }, 500);
        })
      );
    }
  };

  const handleCloseSnackbar = () => {
    setOpenSnackbar(false);
    setErrorMessage("");
  };

  return (
    <>
      <Modal open={isOpen} onClose={onClose}>
        <ModalBox component="form" sx={{
          width: { xs: '95%', sm: '400px', md: '500px' },
          maxHeight: { xs: '95vh', sm: '90vh' }, padding: { xs: 2, md: 4 }
        }} onSubmit={handleSubmit}>
          <Typography variant="h2">{modalTitle}</Typography>

          {/* Logo. Upload needs a workspace to attach the asset to, so in the
              create flow this is a preview with a note rather than a control. */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mt: 3 }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                flexShrink: 0,
                borderRadius: "14px",
                overflow: "hidden",
                backgroundColor: logoUrl ? "#fff" : "rgba(99, 102, 241, 0.12)",
                border: "1px solid rgba(226, 232, 240, 0.9)",
                color: "#4f46e5",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 700,
                fontSize: 20,
              }}
            >
              {logoUploading ? (
                <CircularProgress size={22} />
              ) : logoUrl ? (
                <Box
                  component="img"
                  src={logoUrl}
                  alt=""
                  sx={{ width: "100%", height: "100%", objectFit: "cover" }}
                />
              ) : (
                monogramFor(workspace.name)
              )}
            </Box>

            <Box sx={{ minWidth: 0 }}>
              {isEdit ? (
                <>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={logoUploading}
                      onClick={() => fileInputRef.current?.click()}
                      sx={{ textTransform: "none", borderRadius: "10px" }}
                    >
                      {logoUrl ? "Replace logo" : "Upload logo"}
                    </Button>
                    {logoUrl && (
                      <Button
                        size="small"
                        disabled={logoUploading}
                        onClick={handleLogoRemove}
                        sx={{ textTransform: "none", borderRadius: "10px", color: "#64748b" }}
                      >
                        Remove
                      </Button>
                    )}
                  </Box>
                  <Typography sx={{ fontSize: 12, color: "#94a3b8", mt: 0.75 }}>
                    PNG, JPG, WEBP or SVG, up to {LOGO_MAX_MB}MB. A square image works best.
                  </Typography>
                </>
              ) : (
                <Typography sx={{ fontSize: 12, color: "#94a3b8" }}>
                  Your workspace starts with these initials. You can upload a logo once it
                  has been created.
                </Typography>
              )}
              <input
                ref={fileInputRef}
                type="file"
                accept={LOGO_TYPES.join(",")}
                onChange={handleLogoSelect}
                style={{ display: "none" }}
              />
            </Box>
          </Box>

          <Box sx={{ marginTop: 2 }}>
            <InputLabel shrink htmlFor="name">
              Workspace Name
            </InputLabel>
            <OutlinedInput
              fullWidth
              value={workspace.name}
              onChange={handleChange}
              name="name"
            />
            {createError && (
              <div className="mt-2 text-sm text-red-600">
                {createError}
              </div>
            )}
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 2,
                marginBottom: 2,
              }}
            >
              <OutlinedAddButton
                label="Back"
                sx={{ mr: 1 }}
                component={Link}
                onClick={onClose}
                variant="outlined"
                color="primary"
                startIcon={<ReplayIcon />}
              />

              <AddButton
                label={buttonText}
                sx={{ mr: 1 }}
                type="submit"
              />
            </Box>
          </Box>
        </ModalBox>
      </Modal>
      <Snackbar
        open={openSnackbar}
        autoHideDuration={5000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={severity}
          sx={{ width: "100%" }}
          variant="filled"
        >
          {severity === "success" ? successMessage : errorMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default WorkspaceModal;
